import { StoreListItem } from '@/class/StoreListItem';
import { HTTPResponse } from '@/class/HTTPResponse';
import { fetchData, postData, putData } from '../../../lib/api';
import { buildCsv, downloadCsv, formatDateForFilename } from '../../../lib/csv';
import {
  CITY_OTHER,
  isTokyo23Ward,
  PREFECTURES,
  TOKYO_23_WARDS,
} from '../../../lib/regions';
import SystemAdminLayout from '../../layout/SystemAdminLayout';
import StoreCreateModal from './createModal';
import StoreEditModal from './editModal';
import {
  emptySystemAdminStoreForm,
  parseSystemAdminStoreEditForm,
  parseSystemAdminStoreForm,
  storeToEditForm,
  STORE_GRADE_OPTIONS,
  SystemAdminStoreForm,
} from './storeForm';
import {
  adminCard,
  adminTableCell,
  adminTableHead,
  adminTableWrap,
  adminButtonPrimary,
  adminFilterRow,
  adminInput,
  adminPageToolbar,
  adminSectionTitleToolbar,
} from '../../layout/adminStyles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const tableCellWrap =
  'border border-stone-200 p-2 sm:p-3 text-stone-700 text-sm align-top whitespace-normal break-words';

type SortableStoreKey = 'pref' | 'city';
type SortDirection = 'asc' | 'desc';

const SORTABLE_COLUMNS: SortableStoreKey[] = ['pref', 'city'];

const formatTime = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const match = value.match(/(\d{1,2}):(\d{2})/);
  if (!match) {
    return value;
  }

  return `${match[1]}:${match[2]}`;
};

const formatBoolean = (value: boolean): string => (value ? 'あり' : 'なし');

const formatGrade = (grade?: number | null): string => {
  const option = STORE_GRADE_OPTIONS.find((item) => Number(item.value) === grade);
  return option?.label ?? '—';
};

const displayValue = (value?: string | number | null): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  return String(value);
};

const formatCoordinate = (value?: number | null): string => {
  if (value === null || value === undefined) {
    return '—';
  }

  return value.toFixed(6);
};

const csvCell = (value?: string | number | null): string => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value);
};

const compareStoreValues = (
  a: StoreListItem,
  b: StoreListItem,
  key: SortableStoreKey,
  direction: SortDirection
): number => {
  const left = String(a[key] ?? '');
  const right = String(b[key] ?? '');
  const result = left.localeCompare(right, 'ja');

  if (result !== 0) {
    return direction === 'asc' ? result : -result;
  }

  return a.id - b.id;
};

const COLUMNS: Array<{
  key: keyof StoreListItem;
  label: string;
  render?: (store: StoreListItem) => React.ReactNode;
  csvValue?: (store: StoreListItem) => string | number;
}> = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: '店舗名' },
  { key: 'furigana', label: 'ふりがな', csvValue: (store) => csvCell(store.furigana) },
  { key: 'grade', label: 'プラン', render: (store) => formatGrade(store.grade), csvValue: (store) => formatGrade(store.grade) },
  { key: 'zip', label: '郵便番号' },
  { key: 'pref', label: '都道府県' },
  { key: 'city', label: '市区町村' },
  { key: 'street_address', label: '住所' },
  { key: 'building', label: '建物', csvValue: (store) => csvCell(store.building) },
  {
    key: 'latitude',
    label: '緯度',
    render: (store) => formatCoordinate(store.latitude),
    csvValue: (store) => (store.latitude == null ? '' : store.latitude.toFixed(6)),
  },
  {
    key: 'longitude',
    label: '経度',
    render: (store) => formatCoordinate(store.longitude),
    csvValue: (store) => (store.longitude == null ? '' : store.longitude.toFixed(6)),
  },
  { key: 'phone_number', label: '電話番号' },
  { key: 'email', label: 'メール' },
  { key: 'login_id', label: 'ログインID' },
  { key: 'billing_zip', label: '請求先郵便番号', csvValue: (store) => csvCell(store.billing_zip) },
  { key: 'billing_pref', label: '請求先都道府県', csvValue: (store) => csvCell(store.billing_pref) },
  { key: 'billing_city', label: '請求先市区町村', csvValue: (store) => csvCell(store.billing_city) },
  { key: 'billing_address', label: '請求先住所', csvValue: (store) => csvCell(store.billing_address) },
  { key: 'billing_building', label: '請求先建物', csvValue: (store) => csvCell(store.billing_building) },
  { key: 'billing_contact_name', label: '請求先担当者', csvValue: (store) => csvCell(store.billing_contact_name) },
  { key: 'billing_email', label: '請求先メール', csvValue: (store) => csvCell(store.billing_email) },
  {
    key: 'open_time',
    label: '開店',
    render: (store) => formatTime(store.open_time),
    csvValue: (store) => formatTime(store.open_time).replace('—', ''),
  },
  {
    key: 'close_time',
    label: '閉店',
    render: (store) => formatTime(store.close_time),
    csvValue: (store) => formatTime(store.close_time).replace('—', ''),
  },
  {
    key: 'has_parking',
    label: '駐車場',
    render: (store) => formatBoolean(store.has_parking),
    csvValue: (store) => formatBoolean(store.has_parking),
  },
];

const SystemAdminStoreIndexPage = () => {
  const [stores, setStores] = useState<StoreListItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isShowCreateModal, setIsShowCreateModal] = useState<boolean>(false);
  const [isShowEditModal, setIsShowEditModal] = useState<boolean>(false);
  const [editingStoreId, setEditingStoreId] = useState<number | null>(null);
  const [createForm, setCreateForm] = useState<SystemAdminStoreForm>(emptySystemAdminStoreForm());
  const [editForm, setEditForm] = useState<SystemAdminStoreForm>(emptySystemAdminStoreForm());
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [sortKey, setSortKey] = useState<SortableStoreKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [prefFilter, setPrefFilter] = useState<string>('');
  const [cityFilter, setCityFilter] = useState<string>('');

  const loadStores = useCallback(async () => {
    setIsLoading(true);
    try {
      const res: HTTPResponse = await fetchData('store');
      setStores(Array.isArray(res.data) ? res.data : []);
    } catch (err: unknown) {
      console.error(err);
      setStores([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const filteredStores = useMemo(() => {
    return stores.filter((store) => {
      if (prefFilter && store.pref !== prefFilter) {
        return false;
      }

      if (!cityFilter) {
        return true;
      }

      if (cityFilter === CITY_OTHER) {
        return !isTokyo23Ward(store.city);
      }

      return store.city === cityFilter;
    });
  }, [stores, prefFilter, cityFilter]);

  const sortedStores = useMemo(() => {
    if (!sortKey) {
      return filteredStores;
    }

    return [...filteredStores].sort((a, b) => compareStoreValues(a, b, sortKey, sortDirection));
  }, [filteredStores, sortKey, sortDirection]);

  const handleSort = (key: SortableStoreKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(key);
    setSortDirection('asc');
  };

  const handleDownloadCsv = () => {
    if (sortedStores.length === 0) {
      alert('ダウンロードする店舗がありません。');
      return;
    }

    const headers = COLUMNS.map((column) => column.label);
    const rows = sortedStores.map((store) =>
      COLUMNS.map((column) => {
        if (column.csvValue) {
          return column.csvValue(store);
        }

        const value = store[column.key];
        if (typeof value === 'boolean') {
          return formatBoolean(value);
        }

        return csvCell(value as string | number | null);
      })
    );

    downloadCsv(`stores_${formatDateForFilename()}.csv`, buildCsv(headers, rows));
  };

  const openCreateModal = () => {
    setCreateForm(emptySystemAdminStoreForm());
    setIsShowCreateModal(true);
  };

  const closeCreateModal = () => {
    if (!isSaving) {
      setIsShowCreateModal(false);
      setCreateForm(emptySystemAdminStoreForm());
    }
  };

  const openEditModal = (store: StoreListItem) => {
    setEditingStoreId(store.id);
    setEditForm(storeToEditForm(store));
    setIsShowEditModal(true);
  };

  const closeEditModal = () => {
    if (!isSaving) {
      setIsShowEditModal(false);
      setEditingStoreId(null);
      setEditForm(emptySystemAdminStoreForm());
    }
  };

  const handleCreate = async () => {
    const payload = parseSystemAdminStoreForm(createForm);
    if (!payload) {
      return;
    }

    setIsSaving(true);
    try {
      const res: HTTPResponse = await postData('store', payload);
      if (res.data) {
        setIsShowCreateModal(false);
        setCreateForm(emptySystemAdminStoreForm());
        await loadStores();
        alert('店舗を登録しました。');
      }
    } catch (err: unknown) {
      console.error(err);
      alert('店舗の登録に失敗しました。入力内容をご確認ください。');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (editingStoreId == null) {
      return;
    }

    const payload = parseSystemAdminStoreEditForm(editForm);
    if (!payload) {
      return;
    }

    setIsSaving(true);
    try {
      const res: HTTPResponse = await putData(`store/${editingStoreId}`, payload);
      if (res.data) {
        closeEditModal();
        await loadStores();
        alert('店舗を更新しました。');
      }
    } catch (err: unknown) {
      console.error(err);
      alert('店舗の更新に失敗しました。入力内容をご確認ください。');
    } finally {
      setIsSaving(false);
    }
  };

  const columnCount = COLUMNS.length + 1;

  return (
    <SystemAdminLayout>
      <div className={adminCard}>
        <div className={adminPageToolbar}>
          <div>
            <h2 className={adminSectionTitleToolbar}>店舗一覧</h2>
            <p className="mt-1 text-sm text-stone-600">
              {prefFilter || cityFilter
                ? `${sortedStores.length} 件 / 全 ${stores.length} 件`
                : `全 ${stores.length} 件`}
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <button
              type="button"
              className={`${adminButtonPrimary} w-full sm:w-auto bg-stone-600 hover:bg-stone-700`}
              onClick={handleDownloadCsv}
              disabled={isLoading || sortedStores.length === 0}
            >
              CSVダウンロード
            </button>
            <button type="button" className={`${adminButtonPrimary} w-full sm:w-auto`} onClick={openCreateModal}>
              新規店舗登録
            </button>
          </div>
        </div>

        <div className={adminFilterRow}>
          <div className="flex w-full flex-col gap-1 sm:w-auto">
            <label htmlFor="store-filter-pref" className="text-sm font-medium text-stone-600">
              都道府県
            </label>
            <select
              id="store-filter-pref"
              className={`${adminInput} w-full sm:min-w-[12rem]`}
              value={prefFilter}
              onChange={(e) => setPrefFilter(e.target.value)}
            >
              <option value="">すべて</option>
              {PREFECTURES.map((pref) => (
                <option key={pref} value={pref}>
                  {pref}
                </option>
              ))}
            </select>
          </div>
          <div className="flex w-full flex-col gap-1 sm:w-auto">
            <label htmlFor="store-filter-city" className="text-sm font-medium text-stone-600">
              市区町村
            </label>
            <select
              id="store-filter-city"
              className={`${adminInput} w-full sm:min-w-[12rem]`}
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
            >
              <option value="">すべて</option>
              {TOKYO_23_WARDS.map((ward) => (
                <option key={ward} value={ward}>
                  {ward}
                </option>
              ))}
              <option value={CITY_OTHER}>{CITY_OTHER}</option>
            </select>
          </div>
        </div>

        <div className={adminTableWrap}>
          <table className="mb-5 w-full min-w-[72rem] border-collapse table-auto">
            <thead>
              <tr className={adminTableHead}>
                {COLUMNS.map((column) => {
                  const isSortable = SORTABLE_COLUMNS.includes(column.key as SortableStoreKey);
                  const isActive = sortKey === column.key;
                  const ariaSort = isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none';

                  if (!isSortable) {
                    return (
                      <th key={column.key} className={`${adminTableCell} ${tableCellWrap} text-left`}>
                        {column.label}
                      </th>
                    );
                  }

                  return (
                    <th
                      key={column.key}
                      className={`${adminTableCell} ${tableCellWrap} text-left`}
                      aria-sort={ariaSort}
                    >
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 font-medium text-stone-700 hover:text-teal-700"
                        onClick={() => handleSort(column.key as SortableStoreKey)}
                      >
                        <span>{column.label}</span>
                        <span className="text-xs text-stone-500" aria-hidden="true">
                          {isActive ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      </button>
                    </th>
                  );
                })}
                <th className={`${adminTableCell} ${tableCellWrap} text-center w-24`}>操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={columnCount} className={`${tableCellWrap} p-6 text-center text-stone-500`}>
                    読み込み中...
                  </td>
                </tr>
              ) : sortedStores.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className={`${tableCellWrap} p-6 text-center text-stone-500`}>
                    {prefFilter || cityFilter ? '条件に一致する店舗がありません。' : '店舗がありません。'}
                  </td>
                </tr>
              ) : (
                sortedStores.map((store: StoreListItem) => (
                  <tr key={store.id} className="bg-white even:bg-stone-50/60">
                    {COLUMNS.map((column) => (
                      <td key={column.key} className={tableCellWrap}>
                        {column.render ? column.render(store) : displayValue(store[column.key] as string | number | null)}
                      </td>
                    ))}
                    <td className={`${tableCellWrap} text-center`}>
                      <button
                        type="button"
                        className={`${adminButtonPrimary} text-sm py-1 px-3`}
                        onClick={() => openEditModal(store)}
                      >
                        編集
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <StoreCreateModal
        isShowModal={isShowCreateModal}
        form={createForm}
        isSaving={isSaving}
        onChange={setCreateForm}
        onClose={closeCreateModal}
        onSubmit={handleCreate}
      />

      <StoreEditModal
        isShowModal={isShowEditModal}
        storeId={editingStoreId}
        form={editForm}
        isSaving={isSaving}
        onChange={setEditForm}
        onClose={closeEditModal}
        onSubmit={handleSave}
      />
    </SystemAdminLayout>
  );
};

export default SystemAdminStoreIndexPage;
